
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserReview, useUserReview } from '@/hooks/useUserReview';
import { useAuth } from '@/context/AuthContext';
import ReviewForm from './ReviewForm';
import { formatDate } from '@/hooks/useProductDetail';

interface UserReviewsDisplayProps {
  userId: string;
  showReviewButton?: boolean;
}

const UserReviewsDisplay: React.FC<UserReviewsDisplayProps> = ({ 
  userId,
  showReviewButton = true
}) => {
  const { user, isAuthenticated } = useAuth();
  const {
    hasReviewed,
    existingReview,
    userReviews,
    averageRating,
    isLoading,
    submitReview,
    isSubmitting,
    reviewFormOpen,
    setReviewFormOpen
  } = useUserReview(userId);

  console.log('UserReviewsDisplay - User ID:', userId);
  console.log('UserReviewsDisplay - User reviews:', userReviews);
  console.log('UserReviewsDisplay - Has reviewed:', hasReviewed);
  console.log('UserReviewsDisplay - Existing review:', existingReview);

  // Cannot review yourself
  const canReview = isAuthenticated && user?.id !== userId;
  
  // Don't show review button if user has already reviewed or cannot review
  const shouldShowReviewButton = showReviewButton && canReview && !hasReviewed;
  const shouldShowEditButton = showReviewButton && canReview && hasReviewed;

  const handleSubmit = (values: { rating: number; comment?: string }) => {
    submitReview(values);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div className="flex items-center mb-4 md:mb-0">
          <div className="flex items-center">
            <div className="mr-2 font-semibold text-lg">
              {averageRating.toFixed(1)}
            </div>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={18}
                  className={`${
                    star <= Math.round(averageRating)
                      ? 'fill-amber-400 text-amber-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="ml-2 text-gray-500 text-sm">
            ({userReviews?.length || 0} {userReviews?.length === 1 ? 'vlerësim' : 'vlerësime'})
          </div>
        </div>
        
        {shouldShowReviewButton && (
          <Button 
            onClick={() => setReviewFormOpen(true)} 
            className="bg-brand-500 hover:bg-brand-600"
          >
            Shkruaj një Vlerësim
          </Button>
        )}
        
        {shouldShowEditButton && (
          <Button 
            onClick={() => setReviewFormOpen(true)} 
            variant="outline"
          >
            Ndrysho Vlerësimin Tënd
          </Button>
        )}
      </div>
      
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-start space-x-4">
                <div className="h-10 w-10 rounded-full bg-gray-200"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-gray-200 rounded"></div>
                  <div className="h-3 w-1/4 bg-gray-200 rounded"></div>
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : userReviews?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nuk ka ende vlerësime. {shouldShowReviewButton && "Bëhu i pari që lë një vlerësim!"}
        </div>
      ) : (
        <div className="space-y-6">
          {userReviews?.map((review) => (
            <ReviewCard 
              key={review.id} 
              review={review} 
              isCurrentUserReview={review.reviewer_id === user?.id}
              onEdit={() => setReviewFormOpen(true)}
            />
          ))}
        </div>
      )}
      
      <Dialog open={reviewFormOpen} onOpenChange={setReviewFormOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{existingReview ? 'Ndrysho Vlerësimin' : 'Shkruaj një Vlerësim'}</DialogTitle>
          </DialogHeader>
          <ReviewForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            existingReview={existingReview}
            onCancel={() => setReviewFormOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface ReviewCardProps {
  review: UserReview;
  isCurrentUserReview: boolean;
  onEdit: () => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, isCurrentUserReview, onEdit }) => {
  console.log('ReviewCard - Review data:', review);
  
  return (
    <div className="border-b pb-4">
      <div className="flex justify-between">
        <div className="flex items-center">
          <Avatar className="h-10 w-10 mr-3">
            <AvatarImage 
              src={review.reviewer?.profile_image || `https://avatar.vercel.sh/${review.reviewer?.username || review.reviewer_id}`} 
              alt={review.reviewer?.full_name || review.reviewer?.username || 'Përdorues'} 
            />
            <AvatarFallback>
              {(review.reviewer?.full_name?.[0] || review.reviewer?.username?.[0] || 'P').toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {review.reviewer?.full_name || review.reviewer?.username || 'Përdorues Anonim'}
              {isCurrentUserReview && <span className="ml-2 text-xs text-gray-500">(Ti)</span>}
            </div>
            <div className="flex items-center">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={14}
                    className={`${
                      star <= review.rating
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500 ml-2">
                {formatDate(review.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        {isCurrentUserReview && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Ndrysho
          </Button>
        )}
      </div>
      
      {review.comment && (
        <div className="mt-2 text-gray-700 pl-0 md:pl-13">
          {review.comment}
        </div>
      )}
    </div>
  );
};

export default UserReviewsDisplay;
